import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, Package, Shield, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { getAdminInvitationByToken, registerAdminFromInvitation } from '@/lib/api';

export default function AdminInviteAcceptPage() {
  const { token } = useParams();
  const { t } = useTranslation();
  const [inv, setInv] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ name: '', password: '', confirm: '' });

  useEffect(() => {
    getAdminInvitationByToken(token)
      .then((r) => setInv(r.data))
      .catch((e) => setError(e?.response?.data?.detail || 'Einladung konnte nicht geladen werden'))
      .finally(() => setLoading(false));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (form.password !== form.confirm) { toast.error(t('adminInvite.mismatch')); return; }
    setSubmitting(true);
    try {
      const res = await registerAdminFromInvitation(token, { name: form.name, password: form.password });
      localStorage.setItem('token', res.data.access_token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${res.data.access_token}`;
      window.location.assign('/admin');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Fehler');
    } finally {
      setSubmitting(false);
    }
  };

  const invalid = inv && inv.status !== 'PENDING';

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4" data-testid="admin-invite-page">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="mx-auto mb-2 flex h-12 w-12 items-center justify-center rounded-full bg-orange-500/10">
            <Package className="h-6 w-6 text-orange-500" />
          </div>
          <CardTitle className="flex items-center justify-center gap-2"><Shield className="h-4 w-4 text-red-500" /> {t('adminInvite.acceptTitle')}</CardTitle>
          {inv && !invalid && <CardDescription>{t('adminInvite.acceptDesc', { by: inv.invited_by_name })}</CardDescription>}
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-orange-500" /></div>
          ) : error || invalid ? (
            <div className="text-center space-y-3" data-testid="admin-invite-invalid">
              <AlertTriangle className="h-8 w-8 text-amber-500 mx-auto" />
              <p className="text-sm text-muted-foreground">{error || t('adminInvite.invalid', { status: inv.status })}</p>
              <Button variant="outline" asChild><Link to="/login">{t('nav.login')}</Link></Button>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4">
              <div className="space-y-1">
                <Label>{t('adminInvite.email')}</Label>
                <Input value={inv.email} disabled data-testid="admin-invite-email" />
              </div>
              <div className="space-y-1">
                <Label htmlFor="ai-name">{t('adminInvite.name')}</Label>
                <Input id="ai-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="admin-invite-name" />
              </div>
              <div className="space-y-1">
                <Label htmlFor="ai-pw">{t('adminInvite.password')}</Label>
                <Input id="ai-pw" type="password" required minLength={8} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="admin-invite-password" />
              </div>
              <div className="space-y-1">
                <Label htmlFor="ai-pw2">{t('adminInvite.confirm')}</Label>
                <Input id="ai-pw2" type="password" required value={form.confirm} onChange={(e) => setForm({ ...form, confirm: e.target.value })} data-testid="admin-invite-confirm" />
              </div>
              <Button type="submit" className="w-full bg-orange-500 hover:bg-orange-600" disabled={submitting} data-testid="admin-invite-submit">
                {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : t('adminInvite.create')}
              </Button>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
